/**
 * Investigation step form (Module 24).
 * Small dialog form for adding a step to an investigation.
 */

import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { TextField } from '@core/components/mui';
import { investigationService } from '../../services/investigationService';
import { useNotificationStore } from '../../store/notificationStore';
import {
  InvestigationStepTypes,
  type InvestigationStepType,
  type AddInvestigationStepRequest,
} from '../../types/investigation';

export interface InvestigationStepFormProps {
  open: boolean;
  investigationId: string;
  defaultOrder?: number;
  onClose: () => void;
  onCreated?: () => void;
}

const stepTypeLabel = (type: InvestigationStepType): string => {
  switch (type) {
    case InvestigationStepTypes.DataCollection:
      return 'Data Collection';
    case InvestigationStepTypes.Analysis:
      return 'Analysis';
    case InvestigationStepTypes.Hypothesis:
      return 'Hypothesis';
    case InvestigationStepTypes.Verification:
      return 'Verification';
    case InvestigationStepTypes.Conclusion:
      return 'Conclusion';
    default:
      return 'Unknown';
  }
};

/**
 * Dialog form for adding a new investigation step.
 */
export const InvestigationStepForm = ({
  open,
  investigationId,
  defaultOrder,
  onClose,
  onCreated,
}: InvestigationStepFormProps) => {
  const { showNotification } = useNotificationStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stepName, setStepName] = useState('');
  const [description, setDescription] = useState('');
  const [stepType, setStepType] = useState<InvestigationStepType>(InvestigationStepTypes.DataCollection);
  const [order, setOrder] = useState<number>(defaultOrder ?? 1);

  const stepTypeOptions = useMemo(
    () => [
      InvestigationStepTypes.DataCollection,
      InvestigationStepTypes.Analysis,
      InvestigationStepTypes.Hypothesis,
      InvestigationStepTypes.Verification,
      InvestigationStepTypes.Conclusion,
    ],
    []
  );

  const handleClose = () => {
    if (saving) return;
    setError(null);
    onClose();
  };

  const handleCreate = async () => {
    setError(null);
    if (!stepName.trim()) {
      setError('Step name is required.');
      return;
    }
    if (!Number.isFinite(order) || order <= 0) {
      setError('Order must be a positive number.');
      return;
    }

    setSaving(true);
    try {
      const request: AddInvestigationStepRequest = {
        stepName: stepName.trim(),
        description: description.trim() || undefined,
        stepType,
        order,
      };

      await investigationService.addInvestigationStep(investigationId, request);
      showNotification('Step added successfully', 'success');
      setStepName('');
      setDescription('');
      setStepType(InvestigationStepTypes.DataCollection);
      setOrder(defaultOrder ?? 1);
      onCreated?.();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to add step';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Investigation Step</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
          <TextField
            label="Step Name"
            value={stepName}
            onChange={(e) => setStepName(e.target.value)}
            fullWidth
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <FormControl fullWidth>
            <InputLabel>Step Type</InputLabel>
            <Select
              label="Step Type"
              value={String(stepType)}
              onChange={(e) => setStepType(Number(e.target.value) as InvestigationStepType)}
            >
              {stepTypeOptions.map((t) => (
                <MenuItem key={t} value={String(t)}>
                  {stepTypeLabel(t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Order"
            type="number"
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving}>
          {saving ? 'Adding…' : 'Add Step'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

